import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';


import {
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Users,
  Play,
  Eye,
  Calendar,
  Clock,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  X,
  Copy,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Test, TestStatus, Subject } from '@/types';
import { testApi, subjectApi } from '@/lib/api';




import { formatLocalDate } from '../utils/dateUtils';

const TEST_TITLE_MAX_LENGTH = 120;

const TestsPage = () => {
  useBatch(); // We need the context but don't use currentBatch directly
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tests, setTests] = useState<Test[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TestStatus | 'ALL'>('ALL');
  const [subjectFilter, setSubjectFilter] = useState<string>('ALL');


  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [cloneTestData, setCloneTestData] = useState({ title: '', description: '' });
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20, // Increased page size for more compact view
    totalElements: 0,
    totalPages: 0
  });
  const [testTypeFilter, setTestTypeFilter] = useState<string>('ALL');
  const [showStatusInfo, setShowStatusInfo] = useState(false);

  // Load tests and subjects
  useEffect(() => {
    loadTests();
    loadSubjects();
  }, [pagination.page, statusFilter, subjectFilter, searchTerm]);

  const loadTests = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: 'updatedAt',
        sortDir: 'desc',
        ...(statusFilter !== 'ALL' && { status: statusFilter }),
        ...(subjectFilter !== 'ALL' && { subject: subjectFilter }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await testApi.getActualTests(params);
      setTests(response.content || []);
      setPagination(prev => ({
        ...prev,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0
      }));
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      console.log('TestsPage: Loading subjects...');
      const subjectsData = await subjectApi.getActive();
      console.log('TestsPage: Raw subjects data:', subjectsData);

      // Handle different response formats
      let subjectList: any[] = [];

      if (Array.isArray(subjectsData)) {
        subjectList = subjectsData;
      } else if (subjectsData && subjectsData.content && Array.isArray(subjectsData.content)) {
        // Handle paginated response
        subjectList = subjectsData.content;
      } else if (subjectsData && typeof subjectsData === 'object') {
        // Handle single object response
        subjectList = [subjectsData];
      } else {
        console.warn('TestsPage: Unexpected subjects data format:', subjectsData);
        subjectList = [];
      }

      // Transform subjects response to match expected format
      const transformedSubjects = subjectList.map((subject: any) => {
        if (typeof subject === 'string') {
          return {
            id: subject.toLowerCase().replace(/\s+/g, '_'),
            name: subject,
            active: true,
            isActive: true
          };
        } else if (subject && typeof subject === 'object') {
          return {
            id: subject.id || subject.name?.toLowerCase().replace(/\s+/g, '_') || 'unknown',
            name: subject.name || subject.id || 'Unknown Subject',
            active: subject.active !== undefined ? subject.active : true,
            isActive: subject.isActive !== undefined ? subject.isActive : true
          };
        } else {
          console.warn('TestsPage: Invalid subject format:', subject);
          return null;
        }
      }).filter(Boolean) as Subject[]; // Remove null entries and cast to Subject[]

      console.log('TestsPage: Transformed subjects:', transformedSubjects);
      setSubjects(transformedSubjects);

      if (transformedSubjects.length === 0) {
        console.warn('TestsPage: No subjects found. This might indicate:');
        console.warn('1. No questions exist in the database');
        console.warn('2. Tenant filtering is preventing subjects from being returned');
        console.warn('3. API endpoint is not working correctly');
        console.warn('4. Database connection issues');

        // Try to provide helpful feedback to user
        console.log('TestsPage: Attempting to diagnose subject loading issue...');
      } else {
        console.log(`TestsPage: Successfully loaded ${transformedSubjects.length} subjects`);
      }
    } catch (error: any) {
      console.error('TestsPage: Failed to load subjects:', error);
      setSubjects([]);

      // Provide detailed error information
      if (error.response) {
        console.error('TestsPage: Server error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('TestsPage: Network error:', error.request);
      } else {
        console.error('TestsPage: Error details:', error.message);
      }
    }
  };



  const handlePublishTest = async (test: Test) => {
    try {
      await testApi.publishTest(test.id);
      loadTests();
    } catch (error) {
      console.error('Failed to publish test:', error);
    }
  };

  const handleDeleteTest = async (test: Test) => {
    const isAssignedTest = test.status === 'ASSIGNED';
    const confirmationMessage = isAssignedTest
      ? `Are you sure you want to revoke assignment for "${test.title}"?`
      : `Are you sure you want to delete "${test.title}"?`;

    if (window.confirm(confirmationMessage)) {
      try {
        await testApi.deleteTest(test.id);
        loadTests();
      } catch (error) {
        console.error(isAssignedTest ? 'Failed to revoke test assignment:' : 'Failed to delete test:', error);
      }
    }
  };

  const handlePreviewTest = (test: Test) => {
    navigate(`/tests/preview/${test.id}`, {
      state: {
        testTitle: test.title,
        mode: 'view'
      }
    });
  };

  const handleAssignTest = (test: Test) => {
    navigate(`/tests/${test.id}/assign`);
  };

  const handleScheduleTest = (test: Test) => {
    navigate(`/tests/${test.id}/schedule`);
  };



  const handleCloneTest = (test: Test) => {
    setSelectedTest(test);
    setCloneTestData({
      title: `${test.title} (Copy)`,
      description: test.description || ''
    });
    setShowCloneDialog(true);
  };

  const handleCloneSubmit = async () => {
    if (!selectedTest || !cloneTestData.title.trim()) return;

    if (cloneTestData.title.trim().length > TEST_TITLE_MAX_LENGTH) {
      toast({
        title: 'Title is too long',
        description: `Test title must be ${TEST_TITLE_MAX_LENGTH} characters or fewer.`,
        variant: 'destructive'
      });
      return;
    }

    try {
      await testApi.cloneTest(selectedTest.id, {
        title: cloneTestData.title.trim(),
        description: cloneTestData.description.trim(),
        includeAssignments: false // Always clone without assignments
      });
      setShowCloneDialog(false);
      setSelectedTest(null);
      setCloneTestData({ title: '', description: '' });
      toast({ title: 'Test cloned successfully', description: `"${cloneTestData.title.trim()}" has been created as a draft.` });
      loadTests(); // Refresh the test list
    } catch (error: any) {
      console.error('Failed to clone test:', error);
      toast({ title: 'Failed to clone test', description: error?.response?.data?.error || error?.message || 'An unexpected error occurred', variant: 'destructive' });
    }
  };

  const getStatusBadge = (test: Test) => {
    // Use displayStatus if available, otherwise fall back to status
    const statusToShow = test.displayStatus || test.status;

    // Handle null/undefined status
    if (!statusToShow) {
      return <StatusBadge status="unknown" />;
    }

    return <StatusBadge status={statusToShow.toLowerCase()} />;
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = test.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.subjectName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Test Management</h1>
        <Button onClick={() => navigate('/tests/create')}>
          <Plus className="mr-2 h-4 w-4" />
          Create Test
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {/* Search and Primary Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tests by title, description, or subject..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as TestStatus | 'ALL')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="ON_DEMAND">On-Demand</SelectItem>
                  <SelectItem value="PUBLISHED">Published</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="FINISHED">Finished</SelectItem>

                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Additional Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Subjects</SelectItem>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={testTypeFilter} onValueChange={setTestTypeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Test Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {(searchTerm || statusFilter !== 'ALL' || subjectFilter !== 'ALL' || testTypeFilter !== 'ALL') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setSubjectFilter('ALL');
                    setTestTypeFilter('ALL');
                  }}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </Button>
              )}

              {/* Status Information Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowStatusInfo(!showStatusInfo)}
                className="flex items-center gap-2"
              >
                <Info className="h-4 w-4" />
                Status Info
                {showStatusInfo ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Status Information Panel */}
      {showStatusInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Test Status Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                  <span className="font-medium">DRAFT</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test is being created. Not visible to students.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="font-medium">SCHEDULED</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test has specific start/end times. Ready for assignment.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="font-medium">ON_DEMAND</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test available within a flexible time window. Ready for assignment.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="font-medium">PUBLISHED</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test is live and accessible to assigned students.
                </p>
              </div>



              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="font-medium">IN_PROGRESS</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test is currently being taken by students.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="font-medium">FINISHED</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test has ended. Results are available for review.
                </p>
              </div>



              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="font-medium">ARCHIVED</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Test is archived and no longer active.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tests ({pagination.totalElements})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-blue-600 mx-auto mb-3"></div>
                <p className="text-muted-foreground text-sm">Loading tests...</p>
              </div>
            </div>
          ) : filteredTests.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No tests found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchTerm || statusFilter !== 'ALL' || subjectFilter !== 'ALL'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating a new test'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="py-2">Test Name</TableHead>
                  <TableHead className="py-2">Exam Type</TableHead>
                  <TableHead className="py-2">Status</TableHead>
                  <TableHead className="py-2">Questions</TableHead>
                  <TableHead className="py-2">Duration</TableHead>
                  <TableHead className="py-2">Assignments</TableHead>
                  <TableHead className="py-2">Created</TableHead>
                  <TableHead className="text-right py-2">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTests.map((test) => (
                  <TableRow key={test.id} className="hover:bg-muted/50">
                    <TableCell className="py-2">
                      <div>
                        <div className="font-medium text-sm">{test.title || 'Untitled Test'}</div>
                        {test.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-xs">
                            {test.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center text-sm">
                        <BookOpen className="mr-1 h-3 w-3 text-muted-foreground" />
                        {test.testTypeCategory === 'CUSTOM' ? 'Custom' : (test.examType || 'Standard')}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      {getStatusBadge(test)}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="text-sm">
                        <span className="font-medium">{test.totalQuestions || 0}</span>
                        {test.difficultyDistribution && (
                          <div className="text-xs text-muted-foreground">
                            E:{test.difficultyDistribution.easyPercentage}%
                            M:{test.difficultyDistribution.mediumPercentage}%
                            H:{test.difficultyDistribution.hardPercentage}%
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center text-sm">
                        <Clock className="mr-1 h-3 w-3 text-muted-foreground" />
                        {test.duration || 0} min
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="text-xs">
                        <div>{test.assignedStudentCount || 0} assigned</div>
                        <div className="text-muted-foreground">
                          {test.completedStudentCount || 0} completed
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Calendar className="mr-1 h-3 w-3" />
                        {test.createdAt ? formatLocalDate(test.createdAt, { month: 'short', day: 'numeric' }) : 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right py-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePreviewTest(test)}>
                            <BookOpen className="mr-2 h-4 w-4" />
                            Preview Questions
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/tests/${test.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCloneTest(test)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Clone Test
                          </DropdownMenuItem>
                          {test.status === 'DRAFT' && (
                            <>
                              <DropdownMenuItem onClick={() => handleScheduleTest(test)}>
                                <Calendar className="mr-2 h-4 w-4" />
                                Schedule Test
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleAssignTest(test)}>
                            <Users className="mr-2 h-4 w-4" />
                            Assign Test
                          </DropdownMenuItem>
                          {(test.status === 'DRAFT' || test.status === 'SCHEDULED' || test.status === 'ON_DEMAND' || test.status === 'ASSIGNED') && (
                            <DropdownMenuItem onClick={() => handlePublishTest(test)}>
                              <Play className="mr-2 h-4 w-4" />
                              Publish Now
                            </DropdownMenuItem>
                          )}

                          {(test.status === 'DRAFT' || test.status === 'SCHEDULED' || test.status === 'ON_DEMAND') && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteTest(test)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          )}
                          {test.status === 'ASSIGNED' && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteTest(test)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Cancel Assignment
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {pagination.page * pagination.size + 1} to{' '}
                {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of{' '}
                {pagination.totalElements} tests
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = pagination.page < 3
                      ? i
                      : pagination.page > pagination.totalPages - 3
                        ? pagination.totalPages - 5 + i
                        : pagination.page - 2 + i;

                    if (pageNum < 0 || pageNum >= pagination.totalPages) return null;

                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>





      {/* Test Clone Dialog */}
      {showCloneDialog && selectedTest && (
        <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Clone Test</DialogTitle>
              <DialogDescription>
                Create a copy of "{selectedTest.title}" without assignments.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="clone-title">Test Title</Label>
                <Input
                  id="clone-title"
                  value={cloneTestData.title}
                  onChange={(e) => setCloneTestData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter test title"
                  maxLength={TEST_TITLE_MAX_LENGTH}
                />
                <p className="text-xs text-muted-foreground">
                  Max {TEST_TITLE_MAX_LENGTH} characters ({cloneTestData.title.length}/{TEST_TITLE_MAX_LENGTH}).
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="clone-description">Description (Optional)</Label>
                <Input
                  id="clone-description"
                  value={cloneTestData.description}
                  onChange={(e) => setCloneTestData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter test description"
                />
              </div>
              <div className="text-sm text-muted-foreground p-3 bg-blue-50 rounded-lg">
                <strong>Note:</strong> The cloned test will include all questions and settings but no assignments.
                It will be created as a draft that you can modify and schedule separately.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloneDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCloneSubmit}
                disabled={!cloneTestData.title.trim() || cloneTestData.title.trim().length > TEST_TITLE_MAX_LENGTH}
              >
                <Copy className="mr-2 h-4 w-4" />
                Clone Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TestsPage;
